import React from "react";
import { useTranslation } from "react-i18next";
import bookingStatusService from "../services/bookingStatusService";
import { formatDate, calculateTimeDifference } from "../utils/utils";
import { BookingHistoryDataProxy } from "@me-team/host/main/graphql/types";
import { useGetServiceByIdQuery } from "@me-team/host/main/appointment/graphql/appointment.hooks";
import { useGetEmployeeByIdQuery } from "@me-team/host/main/addEmployee/graphql/addEmployee.hooks";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { useModulesAccess } from "@me-team/host/src/utils/useModulesAccess";
import { AllModulesTagName } from "../../../utils/enums";
import { useParams } from "react-router-dom";

interface BookingHistoryDataItemProps {
    data: BookingHistoryDataProxy;
    currency: string;
    isCreate: boolean;
    previousStatus?: number | null;
    startTime: string;
}

const BookingHistoryDataItem: React.FC<BookingHistoryDataItemProps> = ({
    data,
    currency,
    isCreate,
    previousStatus = null,
    startTime,
}) => {
    const { t, i18n } = useTranslation();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const enabledWarehousesModule: boolean = useModulesAccess([AllModulesTagName.WAREHOUSES]);

    const isServiceField = data.field === "service";
    const isEmployeeField = data.field === "employee";

    const { data: serviceById, loading: serviceLoading } = useGetServiceByIdQuery({
        variables: {
            id: Number(data.value),
            companyId: Number(currentCompanyId),
        },
        skip: !isServiceField || !currentCompanyId || !data.value,
    });

    const serviceName = serviceById?.user?.company?.services?.services?.[0]?.name ?? "";

    const { data: employeeData, loading: employeeLoading } = useGetEmployeeByIdQuery({
        variables: {
            employeeId: Number(data.value),
            companyId: Number(currentCompanyId),
        },
        context: {
            errorType: "local",
        },
        skip: !isEmployeeField || !currentCompanyId || !data.value,
    });

    const employee = employeeData?.user?.company?.employees?.employees?.[0];

    const renderField = (data: BookingHistoryDataProxy) => {
        switch (data.field) {
            case "status":
                const statusText = bookingStatusService.getStatusDetails(+data.value, previousStatus, isCreate, t);
                return (
                    <>
                        <i className="bi bi-clipboard me-3 text-grey"></i>
                        <span className="text-grey">{t("Status")} :</span>
                        <span className="ms-1" dangerouslySetInnerHTML={{ __html: statusText }}></span>
                    </>
                );
            case "date":
                return (
                    <>
                        <i className="bi bi-calendar4-week me-3 text-grey"></i>
                        <span className="text-grey">{t("Booking date")} :</span>
                        <span className="ms-1">{formatDate(data.value, i18n.language)}</span>
                    </>
                );
            case "isWriteOffCompleted":
                if (!enabledWarehousesModule) return null;
                return (
                    <>
                        <i className="bi bi-file-earmark-check me-3 text-grey"></i>
                        <span className="text-grey">{t("Write-off of consumables")} :</span>
                        <span className="ms-1">{data?.value === "true" ? t("Completed") : t("Not completed")}</span>
                    </>
                );
            case "clientData":
                return (
                    <>
                        <i className="bi bi-person-circle me-3 text-grey"></i>
                        <span className="text-grey">{t("Client")} : </span>
                        <span className="ms-1">{data.value}</span>
                    </>
                );
            case "service":
                if (serviceLoading) return null;

                return (
                    <>
                        <i className="bi bi-bookmark me-3 text-grey"></i>
                        <span className="text-grey">{t("Service")} : </span>
                        <span className="ms-1">{serviceName}</span>
                    </>
                );
            case "time":
                return (
                    <>
                        <i className="bi bi-clock me-3 text-grey"></i>
                        <span className="text-grey">{t("TimeHistoryChange")} : </span>
                        <span className="ms-1">{data.value}</span>
                    </>
                );
            case "price":
                return (
                    <>
                        <i className="bi text-grey  me-3 bi-cash-coin"></i>
                        <span className="text-grey">{t("Price")}: </span>
                        <span className="ms-1">
                            {data.value} {currency}{" "}
                        </span>
                    </>
                );
            case "notificationTime":
                return (
                    <>
                        <i className="bi bi-clock-history me-3 text-grey"></i>
                        <span className="text-grey">{t("Notification time")} : </span>
                        <span className="ms-1">{data.value}</span>
                    </>
                );
            case "notes":
                return (
                    <>
                        <i className="bi bi-pen me-3 text-grey"></i>
                        <span className="text-grey">{t("Notes")} : </span>
                        <span className="ms-1">{data?.value}</span>
                    </>
                );
            case "employee":
                if (employeeLoading) return <></>;
                return (
                    <>
                        <i className="bi bi-person me-3 text-grey"></i>
                        <span className="text-grey">{t("EmployeeHistoryChange")} :</span>
                        <span className="ms-1">
                            {employee?.name} {employee?.surname}
                        </span>
                    </>
                );
            case "endTime":
                const duration = startTime ? calculateTimeDifference(data.value, startTime) : "N/A";
                return (
                    <>
                        <i className="bi bi-clock-history me-3 text-grey"></i>
                        <span className="text-grey">{t("Duration")} : </span>
                        <span className="ms-1">{duration}</span>
                    </>
                );
            default:
                return null;
        }
    };

    return <p className="mb-1 fs-7">{renderField(data)}</p>;
};

export default BookingHistoryDataItem;
